import { Download, FileSpreadsheet, Share2, X } from "lucide-react";
import { useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import type { WorkbookFileResult } from "../trips/workbook";

type BackupShareDialogProps = {
  prepared: WorkbookFileResult;
  canShare: boolean;
  isBusy: boolean;
  onClose: () => void;
  onShare: () => void;
  onDownload: () => void;
};

export function BackupShareDialog({
  prepared,
  canShare,
  isBusy,
  onClose,
  onShare,
  onDownload,
}: BackupShareDialogProps) {
  const primaryActionRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    primaryActionRef.current?.focus();
  }, []);

  useEffect(() => {
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previousOverflow;
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape" && !isBusy) onClose();
    };

    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [isBusy, onClose]);

  return createPortal(
    <div
      className="fixed inset-0 z-[95] flex items-end justify-center bg-[#02040C]/80 p-3 backdrop-blur-sm sm:items-center sm:p-5"
      style={{ paddingBottom: "max(0.75rem, env(safe-area-inset-bottom))" }}
      onMouseDown={(event) => {
        if (event.target === event.currentTarget && !isBusy) onClose();
      }}
    >
      <section
        role="dialog"
        aria-modal="true"
        aria-labelledby="backup-share-title"
        className="orbit-card w-full max-w-md overflow-hidden rounded-2xl"
      >
        <header className="flex items-center justify-between gap-3 border-b border-[rgba(255,255,255,0.07)] px-4 py-4 sm:px-5">
          <div className="flex min-w-0 items-center gap-3">
            <span className="orbit-tone-emerald grid h-11 w-11 shrink-0 place-items-center rounded-xl">
              <FileSpreadsheet size={19} />
            </span>
            <div className="min-w-0">
              <h2 id="backup-share-title" className="orbit-display text-lg font-bold text-slate-950">
                Backup ready
              </h2>
              <p className="mt-0.5 text-sm text-slate-500">
                Trips, payments, and expenses are together in one Excel file.
              </p>
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={isBusy}
            aria-label="Close backup sharing"
            className="orbit-tab grid h-11 w-11 shrink-0 place-items-center rounded-xl disabled:opacity-45"
          >
            <X size={19} />
          </button>
        </header>

        <div className="space-y-4 px-4 py-4 sm:px-5">
          <div className="orbit-raised rounded-xl px-3 py-3">
            <p className="break-all text-sm font-bold text-slate-950">{prepared.file.name}</p>
            <p className="mt-1 text-xs text-slate-500">
              {prepared.recordCount > 0
                ? `${prepared.recordCount} business data rows`
                : "Blank import format with all required columns"}
            </p>
          </div>

          <p className="text-sm leading-6 text-slate-500">
            {canShare
              ? "Use your phone's share sheet to save this backup to Files, Drive, email, or another app. If sharing is blocked, the app downloads it instead."
              : "This browser cannot share Excel files directly. Download the same complete backup instead."}
          </p>

          <div className="grid gap-2">
            {canShare ? (
              <button
                ref={primaryActionRef}
                type="button"
                onClick={onShare}
                disabled={isBusy}
                className="orbit-button-primary min-h-12 px-4 py-2.5 text-sm disabled:opacity-45"
              >
                <Share2 size={17} />
                {isBusy ? "Opening..." : "Open Share Sheet"}
              </button>
            ) : (
              <>
                <button
                  ref={primaryActionRef}
                  type="button"
                  onClick={onDownload}
                  disabled={isBusy}
                  className="orbit-button-primary min-h-12 px-4 py-2.5 text-sm disabled:opacity-45"
                >
                  <Download size={17} />
                  {isBusy ? "Downloading..." : "Download Backup"}
                </button>
                <button
                  type="button"
                  onClick={onClose}
                  disabled={isBusy}
                  className="orbit-button-secondary min-h-12 px-4 py-2.5 text-sm disabled:opacity-45"
                >
                  Cancel
                </button>
              </>
            )}
          </div>
        </div>
      </section>
    </div>,
    document.body
  );
}
